import { MaskedHeading, Reveal, Rise } from "@/components/Motion";
import { ApplyBanner } from "@/pages/TherapyPage";
import { motion } from "framer-motion";

const GLANCE = [["Length", "2 days"], ["CPD", "14 hours"], ["Training", "One-to-one"], ["Format", "Remote or in person"], ["Pace", "Set by you"], ["Fee", "[Fee TBC]"]];

const PARTS = [
  ["01", "Two experiential training days", "2 full days · face-to-face", "Two full face-to-face training days, delivered one-to-one, with 15-minute breakout rooms for paired practice and skills consolidation."],
  ["02", "Optional online track", "Scalable · at your own pace", "An optional, scalable online training track, so you can take the method further remotely, at whatever pace suits you."],
  ["03", "CPD hours", "14 total CPD hours", "The programme is equivalent to 14 total CPD hours."],
];

const ELIGIBILITY = [
  "Registered or accredited with UKCP, BACP, BABCP, HCPC or EU equivalent.",
  "At least two years of post-registration trauma practice.",
  "Comfort with written case formulation in English.",
  "A willingness to practise skills in pairs during breakout sessions.",
];

const muted = { color: "var(--kh-text-muted)" };

const Hero = () => (
  <section className="px-6 sm:px-10 lg:px-14 pt-20 lg:pt-28">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-8">
        <Rise><div className="kh-eyebrow mb-6">The practitioner pathway</div></Rise>
        <MaskedHeading text="Two days. Five sessions. One structured pathway." className="kh-serif text-5xl sm:text-6xl lg:text-7xl leading-[1]" />
        <Rise delay={0.8}>
          <p className="mt-6 text-lg max-w-2xl leading-relaxed" style={muted} data-testid="training-intro">Several courses run throughout the year, entirely remote or at our location. Training is one-to-one, and the pace is set by you.</p>
        </Rise>
      </div>
      <Rise delay={0.4} className="lg:col-span-4">
        <div className="kh-card" data-testid="course-glance-card">
          <div className="kh-mono mb-3">The course at a glance</div>
          <dl className="space-y-3 text-sm">
            {GLANCE.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <dt style={muted}>{k}</dt>
                <dd className="text-right" data-testid={`glance-${k.toLowerCase()}`}>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Rise>
    </div>
  </section>
);

const Structure = () => (
  <section className="px-6 sm:px-10 lg:px-14 py-24 lg:py-32">
    <div className="max-w-7xl mx-auto">
      <Reveal><div className="kh-mono mb-10">How the training is structured</div></Reveal>
      <div className="space-y-10">
        {PARTS.map(([n, title, meta, desc]) => (
          <Reveal key={n}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-10 py-10" style={{ borderTop: "1px solid var(--kh-border)" }} data-testid={`part-${Number(n)}`}>
              <div className="md:col-span-3">
                <div className="kh-mono" style={{ color: "var(--kh-accent)" }}>Part {n}</div>
                <div className="kh-serif text-2xl mt-2">{title}</div>
                <div className="text-xs mt-2" style={muted}>{meta}</div>
              </div>
              <div className="md:col-span-9">
                <p className="text-base leading-relaxed max-w-3xl" style={muted}>{desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const SessionDot = ({ n, optional }) => (
  <motion.div
    className="flex flex-col items-center gap-3"
    initial={{ opacity: 0, scale: 0.6 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.6, delay: n * 0.09, ease: [0.2, 0.8, 0.2, 1] }}
    data-testid={`session-${n}`}
  >
    <div
      className="w-[30px] h-[30px] sm:w-14 sm:h-14 rounded-full flex items-center justify-center kh-serif text-base sm:text-2xl"
      style={optional
        ? { border: "1px dashed var(--kh-accent-soft)", color: "var(--kh-text-muted)" }
        : { background: "var(--kh-accent)", color: "#fff" }}
    >
      {n}
    </div>
  </motion.div>
);

const Sessions = () => (
  <section className="px-6 sm:px-10 lg:px-14 pb-24 lg:pb-32">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
      <Reveal className="lg:col-span-5">
        <div className="kh-mono mb-4">Session breakdown</div>
        <h2 className="kh-serif text-3xl lg:text-4xl leading-tight">Two days for you. A five-session pathway for your clients.</h2>
      </Reveal>
      <div className="lg:col-span-7 space-y-10">
        <Reveal className="kh-card" data-testid="breakdown-practitioners">
          <div className="kh-eyebrow mb-3">For practitioners / students</div>
          <p className="text-base leading-relaxed">The training takes place over 2 full days.</p>
        </Reveal>
        <Reveal delay={0.1} className="kh-card" data-testid="breakdown-clients">
          <div className="kh-eyebrow mb-3">For clients treated using the framework</div>
          <p className="text-base leading-relaxed">The course teaches practitioners to structure and deliver a 5-session* PTG Therapy pathway for clients.</p>
          <div className="mt-8 flex flex-wrap items-center gap-1 sm:gap-3" data-testid="session-pathway">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SessionDot key={n} n={n} optional={n > 5} />)}
          </div>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs" style={muted}>
            <span>Sessions 1–5 · average pathway</span>
            <span>Sessions 6–8 · complex cases</span>
          </div>
          <p className="mt-6 text-xs leading-relaxed" style={muted}>* 5 sessions is the average; complex cases can take up to 8 sessions if a client needs extra time between individual steps.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Eligibility = () => (
  <section className="px-6 sm:px-10 lg:px-14 py-24 lg:py-32" style={{ background: "var(--kh-surface)" }}>
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <Reveal>
          <div className="kh-mono mb-4">Eligibility</div>
          <h2 className="kh-serif text-3xl lg:text-4xl leading-tight">We're honest about who the course is for.</h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-10 overflow-hidden" style={{ borderRadius: "var(--kh-radius)" }}>
          <img src="/images/student-2.jpg" alt="Smiling woman writing study notes at a sunny table" className="w-full aspect-[4/3] object-cover transition-transform duration-[1.2s] hover:scale-[1.04]" data-testid="eligibility-image" />
        </Reveal>
      </div>
      <ul className="lg:col-span-7 space-y-5" data-testid="eligibility-list">
        {ELIGIBILITY.map((item, i) => (
          <Reveal key={item} delay={i * 0.06}>
            <li className="flex gap-4 py-4" style={{ borderBottom: "1px solid var(--kh-border)" }}>
              <span className="kh-mono pt-1" style={{ color: "var(--kh-accent)" }}>✓</span>
              <span className="text-base">{item}</span>
            </li>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export default function TrainingPage() {
  return (
    <main data-testid="training-page">
      <Hero />
      <Structure />
      <Sessions />
      <Eligibility />
      <ApplyBanner />
    </main>
  );
}
